import React, { useContext, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { TailSpin as Loader } from "react-loader-spinner";

import { Modal } from "components/Modal/Modal";
import { ControlContext } from "context/Controls";
import DinMediaLogo from "logos/din-media-logo.png";

// DIN Media corporate identity color.
const DIN_BLUE = "#003f64";

type PurchaseState = "idle" | "processing" | "success";

interface BuyStandardModalProps {
  show: boolean;
  // The "name:version" display string of the standard being bought.
  standard: string | null;
  // Human-readable title (TranslatedStandard.content), if resolved.
  title?: string;
  onClose: () => void;
  onPurchased: (standard: string) => void;
}

// MOCKED purchase flow for a standard sold by our partner DIN Media. No backend
// call happens — a short timer simulates the transaction, then a success
// checkmark animates in and the standard is reported back as owned.
export const BuyStandardModal = ({
  show,
  standard,
  title,
  onClose,
  onPurchased,
}: BuyStandardModalProps) => {
  const { t } = useTranslation();
  const { displayNotification } = useContext(ControlContext);
  const [state, setState] = useState<PurchaseState>("idle");
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  // Every fresh open starts from the idle confirmation step.
  useEffect(() => {
    if (show) {
      setState("idle");
    }
    return clearTimers;
  }, [show]);

  const handleBuy = () => {
    if (!standard) return;
    setState("processing");

    timers.current.push(
      setTimeout(() => {
        setState("success");
        onPurchased(standard);
        displayNotification?.(
          t("risks.ai.standards.buy.success.headline"),
          t("risks.ai.standards.buy.success.notification", { standard }),
          "success",
          true
        );

        timers.current.push(setTimeout(onClose, 1200));
      }, 900)
    );
  };

  const content = (
    <div className="text-center">
      <div className="flex flex-col items-center gap-1">
        <p className="text-xs font-medium text-gray-500">
          {t("risks.ai.standards.buy.partner")}
        </p>
        <img src={DinMediaLogo} alt="DIN Media" className="h-8 w-auto" />
      </div>

      {state === "success" ? (
        <div className="mt-6 flex flex-col items-center">
          <svg
            width={56}
            height={56}
            viewBox="0 0 20 20"
            className="animate-scale-in text-green-600"
          >
            <circle cx={10} cy={10} r={9} fill="currentColor" opacity="0.15" />
            <path
              d="M6 10 L9 13 L14 7"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="mt-3 text-sm font-medium text-gray-900">
            {t("risks.ai.standards.buy.success.headline")}
          </p>
        </div>
      ) : state === "processing" ? (
        <div className="mt-6 flex flex-col items-center">
          <Loader color={DIN_BLUE} height={48} width={48} />
          <p className="mt-3 text-sm text-gray-600">
            {t("risks.ai.standards.buy.processing")}
          </p>
        </div>
      ) : (
        <>
          <p className="mt-4 text-lg font-semibold text-gray-900">
            <Trans
              i18nKey="risks.ai.standards.buy.headline"
              values={{ standard }}
            />
          </p>
          {title && <p className="mt-1 text-sm text-gray-500">{title}</p>}
          <p className="mt-6 text-sm text-gray-600">
            {t("risks.ai.standards.buy.body")}
          </p>

          <div className="mt-6 flex justify-center items-center gap-4">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t("risks.ai.standards.buy.cancel")}
            </button>
            <button
              type="button"
              data-test="buy-standard-cta"
              onClick={handleBuy}
              style={{ backgroundColor: DIN_BLUE }}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-xs font-medium rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              {t("risks.ai.standards.buy.confirm")}
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Modal
      show={show}
      width="1/2"
      // Lock the modal closed mid-transaction so the mock can't be interrupted.
      onClose={state === "idle" ? onClose : () => undefined}
      content={content}
    />
  );
};
